import {CdkDragDrop, moveItemInArray} from '@angular/cdk/drag-drop';
import {Component, ElementRef, Input, QueryList, ViewChild, ViewChildren, ViewEncapsulation} from '@angular/core';
import {FormArray, FormGroup} from '@angular/forms';
import {NgSelectComponent} from '@ng-select/ng-select';
import {delay, of, Subscription, take} from 'rxjs';
import {DocumentTypeCustomBoxes, DocumentTypeCustomBoxesDaum} from '../../../dto/document-listing.dto';
import {IAutoFillUploadFile} from '../create-cc-by-ai-form/ai-cc-import.service';
import {CustomBoxManupilator} from './custom-box-manupilator';

@Component({
  selector: 'app-custom-box',
  templateUrl: './custom-box.component.html',
  styleUrl: './custom-box.component.scss',
  encapsulation: ViewEncapsulation.None

})

export class CustomBoxComponent{
  
  @Input() customBoxes: DocumentTypeCustomBoxes[] = [];
  @Input() mainFormGroup: FormGroup;
  @Input() document_type_custom_boxes_data: DocumentTypeCustomBoxesDaum[] = [];
  @Input() aiProcessedData: IAutoFillUploadFile;
  @ViewChildren('textField') private textFields: QueryList<ElementRef>;
  @ViewChild('docField', { static: false }) set content(content: NgSelectComponent) {
    if (content) {
      this.timeOutSubscription = of(null).
        pipe(
          delay(0),
          take(1)
        ).subscribe(() => {
          this.docField = content;
        })
    }
  }
  public hiddenValueField: number = 0;
  private docField: NgSelectComponent;
  public timeOutSubscription: Subscription;
  constructor(
    private _customBoxManupilator: CustomBoxManupilator
  ) { }

  /**
   * Open droplist
   * @param boxData 
   */
  toggleField(boxData: DocumentTypeCustomBoxesDaum) {
    const fieldType = boxData?.meta_data?.meta_data_type;
    this.hiddenValueField = this.hiddenValueField === boxData?.meta_data?.id ? 0 : boxData?.meta_data?.id;
    
    if (this.hiddenValueField !== 0) {
        this.timeOutSubscription = of(null).
        pipe(
            delay(200),
            take(1)
        ).subscribe(() => {
            this.focusOnFields(fieldType, boxData.meta_data.id);
        });
    }
  }


  
  /**
   * Focus on input fields
   * @param fieldType 
   * @param metaId
   */
  focusOnFields(fieldType: string = '', metaId?: number) {
    if (fieldType == 'text' || fieldType == 'datepicker') {
      // Since only the active row's editable section is rendered (via *ngIf),
      // textFields.first will always be the correct input.
      this.textFields.first?.nativeElement.focus();
    } else if (fieldType == 'droplist') {
      this.docField?.open();
    }
  }




  /**
   * Showing field values with conditions
   * @param field_type 
   * @param fieldObject 
   * @returns Boolean
   */
  shouldShowValues(field_type: string, fieldObject) {
    if ((field_type == 'datepicker' || field_type == 'droplist' || field_type == 'text') && (fieldObject && fieldObject.value && fieldObject.value != '')) {
      return true;
    }
    if (field_type == 'checkbox' && (fieldObject && fieldObject.value != 0)) {
      return true;
    }
    return false;
  }


  
  /**
   * Update text field value
   * @param $event 
   * @param boxData 
   * @param order 
   */
  updateTextFieldValue($event, boxData: DocumentTypeCustomBoxesDaum, order: number){
    const value = $event.target.value ?? '';
    const responseData = {
      ...this.document_type_custom_boxes_data[order],
      custom_boxes_data_id: boxData?.custom_boxes_data_id,
      box_id: boxData?.document_type_box_id,
      document_type_box_id: boxData?.document_type_box_id,
      value: value,
      order: order,
    }
    this.hiddenValueField = 0;
    this.updateCustomBoxData(responseData);
    this.updateState(this.document_type_custom_boxes_data[order]);
  }



  /**
   * Update check box value from custom box
   * @param $event 
   * @param boxData 
   * @param order 
   */
  updateCheckBoxValue($event, boxData: DocumentTypeCustomBoxesDaum, order: number){
      const item = this.document_type_custom_boxes_data.find(item => item?.id === boxData?.id);
      if(item){
          const value = $event.target.checked ? '1' : '0';
          const responseData = {
              ...item,
              custom_boxes_data_id: boxData?.custom_boxes_data_id,
              document_type_box_id: boxData?.document_type_box_id,
              value: value,
              order: order
          }
          this.updateCustomBoxData(responseData);
          this.updateState(responseData);
      }
      this.hiddenValueField = 0;

  }




  /**
   * Update datepicker value
   * @param $event 
   * @param boxData 
   * @param order 
   */
  updateDatepickerValue($event, boxData:  DocumentTypeCustomBoxesDaum, order: number){
    const value = $event.target?.value ?? '';
    const responseData = {
      ...this.document_type_custom_boxes_data[order],
      custom_boxes_data_id: boxData?.custom_boxes_data_id,
      document_type_box_id: boxData?.document_type_box_id,
      value: value,
      order: order
    }
    this.hiddenValueField = 0;
    this.updateCustomBoxData(responseData);
    this.updateState(this.document_type_custom_boxes_data[order]);
  }


  /**
   * Update droplist value from custom box
   * @param $event 
   * @param boxData 
   * @param order 
   */
  updateDroplistValue($event, boxData: DocumentTypeCustomBoxesDaum, order: number){
    const value = $event?.name;
    const responseData = {
      ...this.document_type_custom_boxes_data[order],
      custom_boxes_data_id: boxData?.custom_boxes_data_id,
      document_type_box_id: boxData?.document_type_box_id,
      value: value,
      order: order,
    }
    this.hiddenValueField = 0;
    this.updateCustomBoxData(responseData);
    this.updateState(this.document_type_custom_boxes_data[order]);
  }





  /**
   * Updates the custom box data at the specified order with the provided response data.
   *
   * @param {Partial<DocumentTypeCustomBoxesDaum>} responseData - The partial data to update the custom box with.
   * @returns {void}
   */
  updateCustomBoxData(responseData:Partial<DocumentTypeCustomBoxesDaum>): void {
    this.document_type_custom_boxes_data = this.document_type_custom_boxes_data.map((item) => {
      if (item.id === responseData.id) {
        return {
          ...item,
          ...responseData
        };
      }
      return item;
    });
  }


  updateState(boxInnerResult: DocumentTypeCustomBoxesDaum) {
    const customBoxesArray = this.mainFormGroup?.get('customBoxes') as FormArray;
    if (!customBoxesArray) return;

    // Find the specific Box group in the FormArray
    const boxGroup = customBoxesArray.controls.find(control => 
      control.get('id')?.value === boxInnerResult.document_type_box_id
    ) as FormGroup;

    if (boxGroup) {
      const fieldsArray = boxGroup.get('document_type_custom_boxes_data') as FormArray;
      if (fieldsArray) {
        // Find the specific field within that box's FormArray
        const fieldGroup = fieldsArray.controls.find(control => 
          control.get('custom_boxes_data_id')?.value === boxInnerResult.custom_boxes_data_id
        ) as FormGroup;

        if (fieldGroup) {
          fieldGroup.patchValue({ 
            value: boxInnerResult.value,
            order: boxInnerResult.field_order 
          }, { emitEvent: false });
        }
      }
    }
  }



  /**
   * Get drop event
   * @param event
   */
  drop(event: CdkDragDrop<string[]>) {
    const previousIndex = event.previousIndex;
    const currentIndex = event.currentIndex;

    if (previousIndex === currentIndex) return;

    // 1. Reorder local array
    const copiedItems = this.document_type_custom_boxes_data.slice();
    moveItemInArray(copiedItems, previousIndex, currentIndex);
    this.document_type_custom_boxes_data = copiedItems;
    
    // 2. Sync with FormArray
    const customBoxesArray = this.mainFormGroup?.get('customBoxes') as FormArray;
    if (customBoxesArray && this.document_type_custom_boxes_data.length > 0) {
      const boxId = this.document_type_custom_boxes_data[0].document_type_box_id;
      const boxGroup = customBoxesArray.controls.find(control => control.get('id')?.value === boxId) as FormGroup;
      
      if (boxGroup) {
        const fieldsArray = boxGroup.get('document_type_custom_boxes_data') as FormArray;
        if (fieldsArray && fieldsArray.length > previousIndex && fieldsArray.length > currentIndex) {
          const control = fieldsArray.at(previousIndex);
          fieldsArray.removeAt(previousIndex);
          fieldsArray.insert(currentIndex, control);
          
          // 3. Update orders for all items in the local data and sync with main form
          this.document_type_custom_boxes_data.forEach((item, index) => {
            item.field_order = index;
            const fieldControl = fieldsArray.at(index);
            if (fieldControl) {
              fieldControl.patchValue({
                order: index,
                field_order: index
              }, { emitEvent: false });
            }
          });
        }
      }
    }
  }

}
